import { type FC, useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { DateTime } from "luxon";
import {
  Button,
  ButtonVariant,
  Typography,
  TypographyVariant,
} from "../../../../components/ui-kit";
import api from "../../../../util/api";
import {
  NotificationTypes,
  useNotification,
} from "../../../../hooks/useNotification";
import {
  PaymentsInterimBillingInterval,
  SubscriptionOptionEnum,
} from "@shared_packages/toolkit/types";
import { getDefaultTrialDays } from "../../companies/components/SubscriptionWizardModal";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface UsageSinceResponse {
  companyId: string;
  from: string;
  to: string;
  unitCount: number;
  levelUsed: number;
  sample: Array<{ amount: number; completedAt: string; status: string }>;
}

export interface InitialBillingSetupRow {
  companyId: string;
  companyName: string;
  subscriptionOption: SubscriptionOptionEnum;
  subscriptionLevel: string | null;
  subscriptionInterval?: string | null;
  customFlatFeePerTransaction: number | null;
  customPercentageFee: number | null;
  customPercentageFeeMinimum: number | null;
  paymentsInterimBillingInterval: PaymentsInterimBillingInterval | null;
  hasBespokePricing?: boolean;
  hasHighRiskSubscriptionPricing?: boolean;
  discountPercentOff?: number | null;
  discountDuration?: string | null;
  discountDurationInMonths?: number | null;
  trialPeriodMode?: string | null;
  trialPeriodDays?: number | null;
}

interface Props {
  row: InitialBillingSetupRow;
  onClose: () => void;
  onApplied: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Styled
// ─────────────────────────────────────────────────────────────────────────────

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(13, 27, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 14px;
  width: 100%;
  max-width: 560px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 28px 32px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  font-family: Montserrat, sans-serif;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #13273f;
`;

const SubTitle = styled.div`
  font-size: 13px;
  color: #6b7280;
  margin-top: 4px;
`;

const Section = styled.div`
  margin-top: 22px;
`;

const SectionTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #6b7280;
  margin-bottom: 10px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 12px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: #495b6c;
  line-height: 1.3;
`;

const FieldHint = styled.span`
  font-size: 11px;
  color: #8b95a2;
  font-weight: 400;
  margin-left: 4px;
`;

const Input = styled.input`
  width: 100%;
  height: 38px;
  padding: 0 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: #027aff;
  }
`;

const Select = styled.select`
  width: 100%;
  height: 38px;
  padding: 0 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  background: #fff;
  font-family: Montserrat, sans-serif;
  box-sizing: border-box;
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #495b6c;
  cursor: pointer;
  margin-bottom: 8px;
  & input {
    margin: 0;
  }
`;

const PreviewBox = styled.div`
  background: #f8fafc;
  border: 1px solid #e0e6ee;
  border-radius: 10px;
  padding: 14px 16px;
`;

const PreviewRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  padding: 4px 0;
  &:not(:last-child) {
    border-bottom: 1px dashed #e5e7eb;
  }
`;

const PreviewLabel = styled.span`
  color: #6b7280;
`;

const PreviewValue = styled.span`
  color: #13273f;
  font-weight: 600;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 24px;
`;

const Warning = styled.div`
  font-size: 12px;
  color: #92400e;
  background: #fff8ec;
  border: 1px solid #fcd34d;
  border-radius: 8px;
  padding: 8px 12px;
  margin-top: 8px;
`;

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const fp = (pence: number) => `£${(pence / 100).toFixed(2)}`;

function startOfCurrentMonthIso(): string {
  return DateTime.now().startOf("month").toISODate() ?? "";
}

const safeFloat = (s: string): number | null => {
  if (!s.trim()) return null;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
};
const safeInt = (s: string): number | null => {
  if (!s.trim()) return null;
  const n = parseInt(s, 10);
  return Number.isFinite(n) ? n : null;
};

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

const InitialBillingSetupModal: FC<Props> = ({ row, onClose, onApplied }) => {
  const { showNotification } = useNotification();
  const isUsageBased =
    row.subscriptionOption === SubscriptionOptionEnum.PAYMENTS_ONLY;

  const [countUsageFrom, setCountUsageFrom] = useState<string>(() =>
    startOfCurrentMonthIso()
  );
  const [usage, setUsage] = useState<UsageSinceResponse | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  // Pricing — strings for input control, parsed on submit.
  const [customFlat, setCustomFlat] = useState<string>(
    row.customFlatFeePerTransaction != null
      ? (row.customFlatFeePerTransaction / 100).toFixed(2)
      : ""
  );
  const [customPct, setCustomPct] = useState<string>(
    row.customPercentageFee != null ? String(row.customPercentageFee) : ""
  );
  const [customMin, setCustomMin] = useState<string>(
    row.customPercentageFeeMinimum != null
      ? (row.customPercentageFeeMinimum / 100).toFixed(2)
      : ""
  );
  const [interimInterval, setInterimInterval] = useState<
    PaymentsInterimBillingInterval | ""
  >(row.paymentsInterimBillingInterval ?? "");

  // Discount
  const [discountPct, setDiscountPct] = useState<string>(
    row.discountPercentOff != null ? String(row.discountPercentOff) : ""
  );
  const [discountDuration, setDiscountDuration] = useState<string>(
    row.discountDuration ?? ""
  );
  const [discountMonths, setDiscountMonths] = useState<string>(
    row.discountDurationInMonths != null
      ? String(row.discountDurationInMonths)
      : ""
  );

  // Pricing flags
  const [bespoke, setBespoke] = useState<boolean>(!!row.hasBespokePricing);
  const [highRisk, setHighRisk] = useState<boolean>(
    !!row.hasHighRiskSubscriptionPricing
  );

  // Trial config (affects future renewals if the customer ever cancels +
  // re-subscribes, less relevant for cutover but useful to expose).
  const [trialMode, setTrialMode] = useState<string>(row.trialPeriodMode ?? "");
  const [trialDays, setTrialDays] = useState<string>(
    row.trialPeriodDays != null ? String(row.trialPeriodDays) : ""
  );

  // Plan / tier / interval — only shown for fixed-price subs
  // (TOOLKIT / TOOLKIT_MOBILE). The PAYMENTS_ONLY view doesn't surface
  // these because tier doesn't determine price for usage-based plans.
  // Allows ops to confirm or correct what plan a manually-billed
  // customer is actually on before the cron starts charging.
  const [subscriptionOption, setSubscriptionOption] =
    useState<SubscriptionOptionEnum>(
      row.subscriptionOption || SubscriptionOptionEnum.TOOLKIT
    );
  const [subscriptionLevel, setSubscriptionLevel] = useState<string>(
    row.subscriptionLevel || "BASIC"
  );
  const [subscriptionInterval, setSubscriptionInterval] = useState<string>(
    (row.subscriptionInterval || "MONTH").toUpperCase()
  );

  const refreshUsage = useCallback(async () => {
    if (!isUsageBased) {
      setUsage(null);
      return;
    }
    if (!countUsageFrom) return;
    try {
      setUsageLoading(true);
      const fromIso = DateTime.fromISO(countUsageFrom).startOf("day").toISO();
      if (!fromIso) return;
      const result = (await api.getUsageSince(
        row.companyId,
        fromIso
      )) as UsageSinceResponse;
      setUsage(result);
    } catch (err) {
      console.error("[InitialBillingSetup] usage-since failed", err);
    } finally {
      setUsageLoading(false);
    }
  }, [countUsageFrom, isUsageBased, row.companyId]);

  useEffect(() => {
    refreshUsage();
  }, [refreshUsage]);

  const handleApply = async () => {
    setApplying(true);
    try {
      const fromIso = DateTime.fromISO(countUsageFrom).startOf("day").toISO();
      if (!fromIso) {
        showNotification("Pick a valid date", NotificationTypes.ERROR);
        return;
      }

      const body: Parameters<typeof api.applyInitialBillingSetup>[1] = {
        countUsageFrom: fromIso,
      };

      // Usage-based pricing config is ONLY relevant for PAYMENTS_ONLY.
      // For TOOLKIT / TOOLKIT_MOBILE the price is fixed by plan/tier, so
      // skipping these keeps the form aligned with the displayed sections
      // (we hide the inputs entirely for fixed-price subs below).
      if (isUsageBased) {
        const flat = safeFloat(customFlat);
        if (flat !== null)
          body.customFlatFeePerTransaction = Math.round(flat * 100);
        const pct = safeFloat(customPct);
        if (pct !== null) body.customPercentageFee = pct;
        const min = safeFloat(customMin);
        if (min !== null)
          body.customPercentageFeeMinimum = Math.round(min * 100);
        body.paymentsInterimBillingInterval = interimInterval || null;
        (body as any).hasBespokePricing = bespoke;
        (body as any).hasHighRiskSubscriptionPricing = highRisk;
      }

      // Plan / tier / interval — fixed-price subs only. Lets the admin
      // confirm or correct what plan the customer is on before the cron
      // starts charging.
      if (!isUsageBased) {
        (body as any).subscriptionOption = subscriptionOption;
        (body as any).subscriptionLevel = subscriptionLevel;
        (body as any).subscriptionInterval = subscriptionInterval;
      }

      // Discount applies to BOTH usage-based and fixed-price subs.
      const dpct = safeFloat(discountPct);
      if (dpct !== null) (body as any).discountPercentOff = dpct;
      if (discountDuration) (body as any).discountDuration = discountDuration;
      const dmonths = safeInt(discountMonths);
      if (dmonths !== null) (body as any).discountDurationInMonths = dmonths;

      // Trial config (rarely needed at cutover, but doesn't hurt to keep
      // editable for both flavours).
      if (trialMode) (body as any).trialPeriodMode = trialMode;
      const tdays = safeInt(trialDays);
      if (tdays !== null) (body as any).trialPeriodDays = tdays;

      const result = await api.applyInitialBillingSetup(row.companyId, body);
      if (!result.success) {
        showNotification(
          result.error || "Failed to apply setup",
          NotificationTypes.ERROR
        );
        return;
      }
      showNotification("Initial setup applied", NotificationTypes.SUCCESS);
      onApplied();
      onClose();
    } catch (err) {
      console.error("[InitialBillingSetup] apply failed", err);
      showNotification(
        "Failed to apply setup — check console",
        NotificationTypes.ERROR
      );
    } finally {
      setApplying(false);
    }
  };

  return (
    <Backdrop onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Title>Initial billing setup</Title>
        <SubTitle>
          {row.companyName} · {row.subscriptionOption}
          {row.subscriptionLevel ? ` ${row.subscriptionLevel}` : ""}
        </SubTitle>

        <Section>
          <Typography variant={TypographyVariant.BODY6}>
            {isUsageBased
              ? "This company was billed manually before automated billing went live. Pick the date of their last manual charge so the cron knows how far back to count usage when it bills them at the next run. Usage between this date and now will be loaded from real transactions."
              : "Fixed-price plan — the cron will charge the full month at the next run regardless of cutover date. Confirm the plan / tier / interval below and apply; the period will be set to the current calendar month automatically."}
          </Typography>
        </Section>

        {/* ── Cutover date (PAYMENTS_ONLY only) ─────────────────────────
            Fixed-price subs don't need this: we charge the whole period
            no matter what the start date is, so we auto-default to the
            start of the current calendar month and skip the picker. */}
        {isUsageBased && (
          <Section>
            <SectionTitle>Cutover date</SectionTitle>
            <Field>
              <Label htmlFor="from">Count usage from</Label>
              <Input
                id="from"
                type="date"
                value={countUsageFrom}
                onChange={(e) => setCountUsageFrom(e.target.value)}
                max={DateTime.now().toISODate() ?? undefined}
              />
              <FieldHint>
                Defaults to start of current month. Pick the actual last-charge
                date if it was earlier.
              </FieldHint>
            </Field>
          </Section>
        )}

        {/* ── Usage preview ────────────────────────────────────────────── */}
        {isUsageBased && (
          <Section>
            <SectionTitle>Computed usage since that date</SectionTitle>
            <PreviewBox>
              {usageLoading ? (
                <PreviewRow>
                  <PreviewLabel>Loading…</PreviewLabel>
                </PreviewRow>
              ) : usage ? (
                <>
                  <PreviewRow>
                    <PreviewLabel>Transactions</PreviewLabel>
                    <PreviewValue>
                      {usage.unitCount.toLocaleString()}
                    </PreviewValue>
                  </PreviewRow>
                  <PreviewRow>
                    <PreviewLabel>Volume</PreviewLabel>
                    <PreviewValue>{fp(usage.levelUsed)}</PreviewValue>
                  </PreviewRow>
                  <PreviewRow>
                    <PreviewLabel>Window</PreviewLabel>
                    <PreviewValue>
                      {DateTime.fromISO(usage.from).toFormat("d LLL yyyy")} →{" "}
                      {DateTime.fromISO(usage.to).toFormat("d LLL yyyy")}
                    </PreviewValue>
                  </PreviewRow>
                  {usage.unitCount === 0 && (
                    <Warning>
                      No paid transactions found in this window. Verify the date
                      — if you trust an offline figure, edit `levelUsed`
                      directly in the DB or adjust via the wizard after apply.
                    </Warning>
                  )}
                </>
              ) : (
                <PreviewRow>
                  <PreviewLabel>—</PreviewLabel>
                </PreviewRow>
              )}
            </PreviewBox>
          </Section>
        )}

        {/* ── Plan & tier (TOOLKIT / TOOLKIT_MOBILE only) ─────────────── */}
        {!isUsageBased && (
          <Section>
            <SectionTitle>Plan & tier</SectionTitle>
            <Typography
              variant={TypographyVariant.BODY6}
              style={{ marginBottom: 12, color: "#6b7280" }}
            >
              Fixed-price plan — no custom fee config. Confirm the subscription
              option, tier and billing interval; the cron will charge the
              matching plan price at the next run.
            </Typography>
            <Row>
              <Field>
                <Label htmlFor="subOption">Subscription</Label>
                <Select
                  id="subOption"
                  value={subscriptionOption}
                  onChange={(e) =>
                    setSubscriptionOption(
                      e.target.value as SubscriptionOptionEnum
                    )
                  }
                >
                  <option value={SubscriptionOptionEnum.TOOLKIT}>
                    TOOLKIT
                  </option>
                  <option value={SubscriptionOptionEnum.TOOLKIT_MOBILE}>
                    TOOLKIT_MOBILE
                  </option>
                </Select>
              </Field>
              <Field>
                <Label htmlFor="subLevel">Tier</Label>
                <Select
                  id="subLevel"
                  value={subscriptionLevel}
                  onChange={(e) => setSubscriptionLevel(e.target.value)}
                >
                  <option value="BASIC">BASIC</option>
                  <option value="STARTER">STARTER</option>
                  <option value="SCALE_UP">SCALE_UP</option>
                  <option value="PROFESSIONAL">PROFESSIONAL</option>
                  <option value="ENTERPRISE">ENTERPRISE</option>
                </Select>
              </Field>
            </Row>
            <Field>
              <Label htmlFor="subInterval">Billing interval</Label>
              <Select
                id="subInterval"
                value={subscriptionInterval}
                onChange={(e) => setSubscriptionInterval(e.target.value)}
              >
                <option value="MONTH">Monthly</option>
                <option value="YEAR">Annual</option>
              </Select>
            </Field>
          </Section>
        )}

        {/* ── Pricing rule (PAYMENTS_ONLY only) ───────────────────────── */}
        {isUsageBased && (
          <Section>
            <SectionTitle>Pricing rule</SectionTitle>
            <Row>
              <Field>
                <Label htmlFor="flat">
                  Flat fee / tx <FieldHint>(£)</FieldHint>
                </Label>
                <Input
                  id="flat"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="e.g. 0.05"
                  value={customFlat}
                  onChange={(e) => setCustomFlat(e.target.value)}
                />
              </Field>
              <Field>
                <Label htmlFor="pct">
                  Percentage <FieldHint>(%)</FieldHint>
                </Label>
                <Input
                  id="pct"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="e.g. 0.5"
                  value={customPct}
                  onChange={(e) => setCustomPct(e.target.value)}
                />
              </Field>
            </Row>
            <Row>
              <Field>
                <Label htmlFor="min">
                  Monthly minimum <FieldHint>(£)</FieldHint>
                </Label>
                <Input
                  id="min"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="e.g. 25.00"
                  value={customMin}
                  onChange={(e) => setCustomMin(e.target.value)}
                />
              </Field>
              <Field>
                <Label htmlFor="interim">Interim billing</Label>
                <Select
                  id="interim"
                  value={interimInterval}
                  onChange={(e) =>
                    setInterimInterval(
                      e.target.value as PaymentsInterimBillingInterval | ""
                    )
                  }
                >
                  <option value="">None (end-of-period only)</option>
                  <option value={PaymentsInterimBillingInterval.DAILY}>
                    Daily
                  </option>
                  <option value={PaymentsInterimBillingInterval.WEEKLY}>
                    Weekly
                  </option>
                  <option value={PaymentsInterimBillingInterval.BIWEEKLY}>
                    Biweekly
                  </option>
                </Select>
              </Field>
            </Row>
          </Section>
        )}

        {/* ── Discount ────────────────────────────────────────────────── */}
        <Section>
          <SectionTitle>Discount (optional)</SectionTitle>
          <Row>
            <Field>
              <Label htmlFor="dpct">
                % off <FieldHint>(0–100)</FieldHint>
              </Label>
              <Input
                id="dpct"
                type="number"
                step="0.01"
                min="0"
                max="100"
                placeholder="e.g. 20"
                value={discountPct}
                onChange={(e) => setDiscountPct(e.target.value)}
              />
            </Field>
            <Field>
              <Label htmlFor="ddur">Duration</Label>
              <Select
                id="ddur"
                value={discountDuration}
                onChange={(e) => setDiscountDuration(e.target.value)}
              >
                <option value="">No discount</option>
                <option value="once">Once</option>
                <option value="repeating">Repeating</option>
                <option value="forever">Forever</option>
              </Select>
            </Field>
          </Row>
          {discountDuration === "repeating" && (
            <Field>
              <Label htmlFor="dmonths">Duration in months</Label>
              <Input
                id="dmonths"
                type="number"
                step="1"
                min="1"
                placeholder="e.g. 3"
                value={discountMonths}
                onChange={(e) => setDiscountMonths(e.target.value)}
              />
            </Field>
          )}
        </Section>

        {/* ── Pricing flags (PAYMENTS_ONLY only — bespoke + high-risk
            both affect tier-based fee math, not flat plan prices) ──── */}
        {isUsageBased && (
          <Section>
            <SectionTitle>Pricing flags</SectionTitle>
            <CheckboxRow>
              <input
                type="checkbox"
                checked={bespoke}
                onChange={(e) => setBespoke(e.target.checked)}
              />
              Bespoke pricing
              <FieldHint>(opaque amount, managed by account team)</FieldHint>
            </CheckboxRow>
            <CheckboxRow>
              <input
                type="checkbox"
                checked={highRisk}
                onChange={(e) => setHighRisk(e.target.checked)}
              />
              High-risk subscription pricing
              <FieldHint>(elevated tier rates)</FieldHint>
            </CheckboxRow>
          </Section>
        )}

        {/* ── Trial config ────────────────────────────────────────────── */}
        <Section>
          <SectionTitle>
            Trial configuration (for future re-subscribes)
          </SectionTitle>
          <Row>
            <Field>
              <Label htmlFor="tmode">Trial mode</Label>
              <Select
                id="tmode"
                value={trialMode}
                onChange={(e) => setTrialMode(e.target.value)}
              >
                <option value="">
                  {`Default (${getDefaultTrialDays(
                    subscriptionOption,
                    highRisk
                  )}d)`}
                </option>
                <option value="default">
                  {`Default (${getDefaultTrialDays(
                    subscriptionOption,
                    highRisk
                  )}d)`}
                </option>
                <option value="custom">Custom days</option>
                <option value="none">No trial</option>
              </Select>
            </Field>
            <Field>
              <Label htmlFor="tdays">Custom days</Label>
              <Input
                id="tdays"
                type="number"
                step="1"
                min="0"
                placeholder="only when Custom"
                disabled={trialMode !== "custom"}
                value={trialDays}
                onChange={(e) => setTrialDays(e.target.value)}
              />
            </Field>
          </Row>
        </Section>

        <Warning>
          Auto-charge stays disabled after you apply — flip the switch on the
          dashboard once you have reviewed the resulting preview.
        </Warning>

        <Buttons>
          <Button variant={ButtonVariant.OUTLINED} onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleApply} loading={applying}>
            Apply initial setup
          </Button>
        </Buttons>
      </Modal>
    </Backdrop>
  );
};

export default InitialBillingSetupModal;
